import { Plus } from "lucide-react";

export type FaqItem = { question: string; answer: string };

export function faqJsonLd(items: FaqItem[], url: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      inLanguage: "en-ZA",
      mainEntity: items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    }),
  };
}

export function FaqSection({
  items,
  heading = "Frequently asked questions",
  paragraph,
  tone = "warm",
}: {
  items: FaqItem[];
  heading?: string;
  paragraph?: string;
  tone?: "warm" | "ink";
}) {
  const ink = tone === "ink";
  const fg = ink ? "text-ink-foreground" : "text-foreground";
  const muted = ink ? "text-ink-foreground/80" : "text-foreground/85";
  const border = ink ? "border-ink-foreground/12" : "border-foreground/12";
  const card = ink ? "bg-ink-foreground/[0.04]" : "bg-foreground/[0.03]";

  return (
    <section id="faq" className={`${ink ? "surface-ink" : "surface-warm"} relative py-24`}>
      <div className="mx-auto max-w-4xl px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-strong">FAQ</p>
        <h2 className={`mt-3 font-display text-3xl leading-tight sm:text-4xl ${fg}`}>{heading}</h2>
        {paragraph ? <p className={`mt-4 max-w-2xl text-base ${muted}`}>{paragraph}</p> : null}

        <div className="mt-10 space-y-3">
          {items.map((item) => (
            <details
              key={item.question}
              className={`group rounded-2xl border ${border} ${card} px-5 py-4 backdrop-blur-sm transition-colors open:border-primary/40`}
            >
              <summary className={`flex cursor-pointer list-none items-start justify-between gap-4 font-semibold ${fg}`}>
                <h3 className="text-base sm:text-lg">{item.question}</h3>
                <Plus
                  aria-hidden="true"
                  className="mt-1 size-5 shrink-0 text-primary-strong transition-transform duration-200 group-open:rotate-45"
                />
              </summary>
              <p className={`mt-3 text-sm leading-relaxed sm:text-base ${muted}`}>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
